/**
 * Team display-name resolution.
 *
 * Shared team names are E2EE — the server stores `name_encrypted` plus the IV in
 * `encryption_metadata` and never sees plaintext. Decrypting requires the team's
 * AES key from encryptionStore, so resolution is async while screens render
 * synchronously: callers resolve into state and fall back to `peekTeamName()`
 * for the first paint.
 */

import { decrypt, getKeyForTeam } from "./encryption";
import type { Team } from "../stores/teamStore";

export const PERSONAL_TEAM_NAME = "Personal";
export const SHARED_TEAM_FALLBACK = "Shared Team";
export const UNNAMED_TEAM = "Unnamed Team";

/**
 * Successfully decrypted names, keyed so that a key rotation — which re-encrypts
 * the name under a new key version — misses the cache and re-resolves.
 * Failures are never cached: a later key sync must be able to succeed.
 */
const nameCache = new Map<string, string>();

function cacheKey(team: Team): string {
  return `${team.id}:${team.current_key_version}:${team.name_encrypted ?? ""}`;
}

function placeholderFor(team: Team): string {
  if (team.team_type === "personal") return PERSONAL_TEAM_NAME;
  return team.name_encrypted ? SHARED_TEAM_FALLBACK : UNNAMED_TEAM;
}

/**
 * Synchronous best guess: a previously resolved name, or the placeholder.
 * Safe to call during render.
 */
export function peekTeamName(team: Team): string {
  if (team.team_type === "personal") return PERSONAL_TEAM_NAME;
  return nameCache.get(cacheKey(team)) ?? placeholderFor(team);
}

/**
 * Decrypt a team's name. Never throws — falls back to the placeholder so a
 * missing key degrades to "Shared Team" rather than breaking the screen.
 */
export async function resolveTeamName(team: Team): Promise<string> {
  if (team.team_type === "personal") return PERSONAL_TEAM_NAME;
  if (!team.name_encrypted) return UNNAMED_TEAM;

  const iv = (team.encryption_metadata as { iv?: string } | null)?.iv;
  if (!iv) return SHARED_TEAM_FALLBACK;

  const key = cacheKey(team);
  const cached = nameCache.get(key);
  if (cached) return cached;

  try {
    const teamKey = await getKeyForTeam(team.id);
    const name = (await decrypt(team.name_encrypted, { iv }, teamKey)).trim();
    if (!name) return SHARED_TEAM_FALLBACK;
    nameCache.set(key, name);
    return name;
  } catch {
    return SHARED_TEAM_FALLBACK;
  }
}

/**
 * Resolve a batch of teams into a map of team id → display name.
 */
export async function resolveTeamNames(
  teams: Team[]
): Promise<Record<string, string>> {
  const names = await Promise.all(teams.map((t) => resolveTeamName(t)));
  return Object.fromEntries(teams.map((t, i) => [t.id, names[i]]));
}
